import { Particle } from '../types/algorithm';
import { calculateParticleFitness } from '../utils/fitnessCalculator';
import {
  tournamentSelection,
  singlePointCrossover,
} from './geneticAlgorithm';
import {
  initializeSwarm,
  findGlobalBest,
  updateParticle,
} from './pso';
import { SeededRNG, createSeededRNG } from '../utils/seededRNG';

/**
 * Generalized Adaptive PSO (GAPSO) implementation from scratch
 * Hybrid of PSO and GA - uses GA operators (crossover) in PSO framework
 */

/**
 * Particle parent selection for the GA phase.
 */
function selectParticleIndex(
  swarm: Particle[],
  selectionRate: number,
  rng: SeededRNG
): number {
  if (!rng.randomChance(selectionRate / 100)) {
    return rng.randomInt(swarm.length);
  }

  // Create temporary chromosome-like structures for tournament selection
  const tempChromosomes = swarm.map(p => ({
    x: p.position,
    fitness: p.fitness,
    rwp: 0,
  }));
  return tournamentSelection(tempChromosomes, rng);
}

/**
 * GAPSO evolution for one generation
 */
function evolveGAPSO(
  swarm: Particle[],
  globalBest: Particle,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  generation: number,
  maxGenerations: number,
  selectionRate: number,
  crossoverRate: number,
  rng: SeededRNG
): void {
  // Linearly decreasing inertia weight
  const wMax = 0.9;
  const wMin = 0.4;
  const w = wMax - ((wMax - wMin) * generation) / maxGenerations;

  const c1 = 2.0;
  const c2 = 2.0;

  // PSO update for all particles
  for (const particle of swarm) {
    updateParticle(particle, globalBest, taskCount, serviceCount, w, c1, c2, rng);
    calculateParticleFitness(particle, matrix, taskCount, serviceCount);
    
    // Update personal best if current fitness is better
    if (particle.fitness > particle.personalBestFitness) {
      particle.personalBestFitness = particle.fitness;
      particle.personalBest = [...particle.position];
    }
  }

  // GA crossover operations (hybrid enhancement)
  // Process pairs of particles for crossover using tournament selection
  const numPairs = Math.floor(swarm.length / 2);
  
  for (let i = 0; i < numPairs; i++) {
    // Apply crossover with crossoverRate probability per mating event
    if (rng.randomChance(crossoverRate / 100)) {
      // Use the shared parent-selection policy for the GA phase.
      const parent1Idx = selectParticleIndex(swarm, selectionRate, rng);
      let parent2Idx = selectParticleIndex(swarm, selectionRate, rng);
      // Ensure different parents (with retry limit to prevent infinite loops)
      let retries = 0;
      const maxRetries = 10;
      while (parent2Idx === parent1Idx && swarm.length > 1 && retries < maxRetries) {
        parent2Idx = selectParticleIndex(swarm, selectionRate, rng);
        retries++;
      }
      // If still same after retries, use adjacent particle
      if (parent2Idx === parent1Idx && swarm.length > 1) {
        parent2Idx = (parent1Idx + 1) % swarm.length;
      }

      // Create temporary chromosomes for crossover
      const parent1 = { 
        x: [...swarm[parent1Idx].position], 
        fitness: swarm[parent1Idx].fitness, 
        rwp: 0 
      };
      const parent2 = { 
        x: [...swarm[parent2Idx].position], 
        fitness: swarm[parent2Idx].fitness, 
        rwp: 0 
      };

      // Get both children from crossover using seeded RNG
      const [child1, child2] = singlePointCrossover(parent1, parent2, taskCount, rng);

      // Create new particles from children with cloned velocity arrays
      const newParticle1: Particle = {
        position: child1.x,
        velocity: [...swarm[parent1Idx].velocity], // Clone velocity array
        personalBest: [...child1.x], // Initialize with child's position
        fitness: 0,
        personalBestFitness: 0,
        rwp: 0,
      };

      const newParticle2: Particle = {
        position: child2.x,
        velocity: [...swarm[parent2Idx].velocity], // Clone velocity array
        personalBest: [...child2.x], // Initialize with child's position
        fitness: 0,
        personalBestFitness: 0,
        rwp: 0,
      };

      // Evaluate children
      calculateParticleFitness(newParticle1, matrix, taskCount, serviceCount);
      calculateParticleFitness(newParticle2, matrix, taskCount, serviceCount);

      // Update personal best for children: compare with parent's personal best, not just current fitness
      // Fixed: Properly initialize personal best by comparing with parent's personal best
      newParticle1.personalBestFitness = Math.max(
        newParticle1.fitness,
        swarm[parent1Idx].personalBestFitness
      );
      if (newParticle1.personalBestFitness === newParticle1.fitness) {
        newParticle1.personalBest = [...newParticle1.position];
      } else {
        newParticle1.personalBest = [...swarm[parent1Idx].personalBest];
      }

      newParticle2.personalBestFitness = Math.max(
        newParticle2.fitness,
        swarm[parent2Idx].personalBestFitness
      );
      if (newParticle2.personalBestFitness === newParticle2.fitness) {
        newParticle2.personalBest = [...newParticle2.position];
      } else {
        newParticle2.personalBest = [...swarm[parent2Idx].personalBest];
      }

      // Replacement policy: replace parents with children if children are better
      if (newParticle1.fitness > swarm[parent1Idx].fitness) {
        swarm[parent1Idx] = newParticle1;
      }
      if (newParticle2.fitness > swarm[parent2Idx].fitness) {
        swarm[parent2Idx] = newParticle2;
      }
    }
  }
}

/**
 * Runs GAPSO for specified generations
 */
export async function runGAPSO(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    selectionRate: number;
    crossoverRate: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void,
  seed?: number,
  runtimeOptions?: {
    yieldForProgress?: boolean;
    reportEveryGeneration?: boolean;
  }
): Promise<Particle> {
  // Create seeded RNG for reproducibility
  const rng = createSeededRNG(seed);
  const shouldYieldForProgress = runtimeOptions?.yieldForProgress ?? true;
  const shouldReportEveryGeneration = runtimeOptions?.reportEveryGeneration ?? false;
  
  // Initialize swarm
  let swarm = initializeSwarm(parameters.populationSize, taskCount, serviceCount, rng);

  // Evaluate initial swarm
  for (const particle of swarm) {
    calculateParticleFitness(particle, matrix, taskCount, serviceCount);
  }

  let globalBest = findGlobalBest(swarm);

  // Evolution loop
  let lastUpdateTime = Date.now();
  const UPDATE_INTERVAL = 100; // Update UI every 100ms (PERFORMANCE OPTIMIZATION)
  
  for (let gen = 0; gen < parameters.generations; gen++) {
    evolveGAPSO(
      swarm,
      globalBest,
      taskCount,
      serviceCount,
      matrix,
      gen,
      parameters.generations,
      parameters.selectionRate,
      parameters.crossoverRate,
      rng
    );
    globalBest = findGlobalBest(swarm);

    // Batch UI updates - only update every 100ms or every 10 generations (PERFORMANCE OPTIMIZATION)
    const now = Date.now();
    const shouldUpdate =
      shouldReportEveryGeneration ||
      (now - lastUpdateTime > UPDATE_INTERVAL) ||
      (gen % 10 === 9) ||
      (gen === parameters.generations - 1);
    
    if (shouldUpdate) {
      if (onGenerationComplete) {
        onGenerationComplete(gen + 1, globalBest.fitness);
      }
      lastUpdateTime = now;
      if (shouldYieldForProgress) {
        // Yield control to allow UI updates when running interactively.
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  }

  return globalBest;
}
